import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    ListItemText,
    MenuItem,
    TextField, Tooltip,
    Typography
} from "@mui/material";
import {useTranslation} from "react-i18next";
import '../../../style/SalesMarketing.css';
import * as React from "react";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import IconButton from "@mui/material/IconButton";
import {CloseRounded} from "@mui/icons-material";
import {useStatusValuesProduct} from "../../../component/useStatusValuesProduct";
import {useProductCodeActions} from "../../../service/useProductCodeActions";
import {createPortal} from 'react-dom';

interface AddProductModalProps {
    open: boolean,
    handleClose: Function,
    categories: any[],
    append: Function,
    fields?: any,
    checkDatesOverlap?: boolean,
    filterProducts: boolean,
    tenantId: string,
    dealerId: string
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        },
    },
};

const selectedProductDetailDefaultValue = {
    productName: "",
    productCode: "",
    startDate: null,
    endDate: null,
    category: "",
    categoryId: "",
    status: "",
    paymentTiming: "",
    jimpyCode: ""
}


const AddProductModal = ({
    open,
    handleClose,
    append,
    categories,
    fields,
    checkDatesOverlap = true,
    filterProducts,
    tenantId,
    dealerId
}: AddProductModalProps) => {
    const {t} = useTranslation(['salesmarketing']);
    const {listProductByCategoryCodeList} = useProductCodeActions();
    const [selectedCategories, setSelectedCategories] = useState<any[]>([]);
    const [selectedProductDetail, setSelectedProductDetail] = useState(selectedProductDetailDefaultValue);
    const [products, setProducts] = useState<any[]>([]);
    const {getStatusLabelProduct} = useStatusValuesProduct();

    const categoriesForTenant = useMemo(() => {
        const uniqueCategories = new Map<string, any>();
        categories
            .filter(cat => cat.tenantId === tenantId)
            .forEach(cat => {
                if (!uniqueCategories.has(cat.categoryCode)) {
                    uniqueCategories.set(cat.categoryCode, cat);
                }
            });
        return Array.from(uniqueCategories.values());
    }, [categories, tenantId]);


    const addProduct = () => {
        append({
            productName: selectedProductDetail.productName,
            productCode: selectedProductDetail.productCode,
            startDate: selectedProductDetail.startDate,
            endDate: selectedProductDetail.endDate,
            category: selectedProductDetail.category,
            status: selectedProductDetail.status,
            paymentTiming: selectedProductDetail.paymentTiming,
            categoryId: selectedProductDetail.categoryId,
            jimpyCode: ''
        })
    }

    const handleChangeSelectedCategories = (event: React.ChangeEvent<HTMLInputElement>) => {
        const {
            target: {value},
        } = event;

        if (value.length === 0) {
            setSelectedProductDetail(selectedProductDetailDefaultValue);
        }
        setSelectedCategories(
            // On autofill we get a stringified value.
            typeof value === 'string' ? value.split(',') : value,
        );
    };

    const handleChangeSelectedProduct = (event: React.ChangeEvent<HTMLInputElement>) => {

        let foundProd = products.find(p => p.productCode === event.target.value)
        setSelectedProductDetail({
            productName: foundProd.name,
            productCode: foundProd.productCode,
            startDate: foundProd.startDate,
            endDate: foundProd.endDate,
            category: categoriesForTenant.find(c => c.categoryCode === foundProd.categoryCode)?.categoryLabel,
            categoryId: foundProd.categoryCode,
            paymentTiming: foundProd.paymentTiming,
            status: foundProd.statusCode,
            jimpyCode: ''
        })
        //}
    };


    const getProducts = () => {
        setSelectedProductDetail(selectedProductDetailDefaultValue);
        if (selectedCategories.length > 0) {
            listProductByCategoryCodeList(selectedCategories).then(res => {
                let products = res.filter((r: any) => r.tenantId === tenantId && r.dealerId === dealerId)
                if (filterProducts) {
                    setProducts(products.filter((r: any) => r.guaranteeSchemeCode[0].id === "NO_CREDIT"))
                } else {
                    setProducts(products)
                }
            }
            )
        } else {
            setProducts([])
        }

    };

    return (
        <Dialog disableEscapeKeyDown maxWidth={"lg"} fullWidth={true} open={open}
            onClose={() => {
                return;
            }}>
            <DialogTitle>
                <Typography sx={{paddingLeft: "10px"}} fontWeight={"bold"} color={"primary"}>
                    {t('salesmarketing:businessAgreement.products.modalTitle')}
                </Typography>
                <IconButton
                    aria-label="close"
                    onClick={() => {
                        handleClose();
                    }}
                    color={"primary"}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                    }}
                >
                    <CloseRounded/>
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Grid container spacing={2} alignItems={"center"}>
                    <Grid item xs={3}>
                        <TextField
                            fullWidth
                            id="selectedCategories"
                            select
                            SelectProps={{
                                MenuProps: MenuProps,
                                multiple: true,
                                renderValue: (selected: any) => selected.map((s: any) => categoriesForTenant.find(ss => ss.categoryCode === s)!.categoryLabel).join(', ')
                            }}
                            label={t('salesmarketing:businessAgreement.products.category')}
                            value={selectedCategories}
                            onChange={handleChangeSelectedCategories}
                            onBlur={() => {
                                getProducts()
                            }}
                        >
                            {categoriesForTenant.map((cat) => (
                                <MenuItem key={cat.categoryCode} value={cat.categoryCode}>
                                    <Checkbox checked={selectedCategories.indexOf(cat.categoryCode) > -1} />
                                    <ListItemText primary={cat.categoryLabel}/>
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={3}>
                        <TextField
                            fullWidth
                            id={"selectedProduct"}
                            label={t('salesmarketing:businessAgreement.products.name')}
                            select
                            value={selectedProductDetail.productCode}
                            onChange={handleChangeSelectedProduct}
                            SelectProps={{MenuProps: MenuProps}}
                        >
                            {products?.filter((p: any) => fields.every((f: any) => f.productCode !== p.productCode)).map((a: any) => {
                                let disabled = checkDatesOverlap ? (!fields.every((f: any) => a.endDate < f.startDate || a.startDate > f.endDate)) : false;
                                return (
                                    <MenuItem
                                        key={a.productCode}
                                        value={a.productCode}
                                        onMouseDownCapture={(e: any) => {
                                            if (disabled) {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                if (e?.nativeEvent && typeof e.nativeEvent.stopImmediatePropagation === 'function') {
                                                    e.nativeEvent.stopImmediatePropagation();
                                                }
                                                return false;
                                            }
                                        }}
                                        onClickCapture={(e: any) => {
                                            if (disabled) {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                if (e?.nativeEvent && typeof e.nativeEvent.stopImmediatePropagation === 'function') {
                                                    e.nativeEvent.stopImmediatePropagation();
                                                }
                                                return false;
                                            }
                                        }}
                                        onMouseDown={(e: any) => {
                                            if (disabled) {
                                                e.preventDefault();
                                                e.stopPropagation();
                                            }
                                        }}
                                        onClick={(e: any) => {
                                            if (disabled) {
                                                e.preventDefault();
                                                e.stopPropagation();
                                            }
                                        }}
                                    >
                                        <Tooltip
                                            title={disabled ? t('salesmarketing:businessAgreement.products.overlap') : ''}
                                            arrow
                                            key={a.productCode}
                                        >
                                            <span
                                                style={{
                                                    width: '100%',
                                                    opacity: disabled ? '0.38' : '1'
                                                }}

                                                onMouseDownCapture={(e: any) => { if (disabled) { e.preventDefault(); e.stopPropagation(); } }}
                                                onClickCapture={(e: any) => { if (disabled) { e.preventDefault(); e.stopPropagation(); } }}
                                                onClick={(e: any) => { if (disabled) e.stopPropagation(); }}
                                            >
                                                {a.name}
                                            </span>
                                        </Tooltip>
                                    </MenuItem>

                                )

                            }
                            )}
                        </TextField>
                    </Grid>
                    <Grid item xs={3}>
                        <TextField
                            disabled
                            fullWidth
                            id="selectedProductCode"
                            label={t('salesmarketing:businessAgreement.products.code')}
                            value={selectedProductDetail.productCode}
                            InputLabelProps={{shrink: true}}
                        />
                    </Grid>
                    <Grid item xs={3}>
                        <TextField
                            disabled
                            fullWidth
                            id="selectedProductStatus"
                            label={t('salesmarketing:businessAgreement.products.status')}
                            value={getStatusLabelProduct(selectedProductDetail.status)}
                            InputLabelProps={{shrink: true}}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions sx={{padding: "15px"}}>
                <Button variant={"outlined"} color={"accent"}
                    onClick={() => {
                        handleClose();
                    }}>
                    {t('salesmarketing:businessAgreement.buttons.cancel')}
                </Button>
                <Button color={"accent"} disabled={selectedProductDetail.productCode === ''} onClick={() => {
                    addProduct();
                    handleClose();
                }}>
                    {t('salesmarketing:businessAgreement.products.add')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default AddProductModal;